import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { navLinks, socialMedia } from '@config';
import { Icon } from '@components/icons';

const StyledHeader = styled.header`
  position: relative;
  z-index: 1;

  @media (min-width: 1024px) {
    position: sticky;
    top: 0;
    display: flex;
    max-height: 100vh;
    width: 50%;
    flex-direction: column;
    justify-content: space-between;
    padding: 96px 0;
  }

  h1 {
    font-size: 36px;
    font-weight: 700;
    letter-spacing: -0.025em;
    color: var(--lightest-slate);
    line-height: 1.1;
    margin: 0;

    a {
      color: inherit;

      &:hover {
        color: inherit;
      }
    }

    @media (min-width: 640px) {
      font-size: 48px;
    }
  }

  .title {
    margin-top: 12px;
    font-size: 18px;
    font-weight: 500;
    letter-spacing: -0.025em;
    color: var(--lightest-slate);
    line-height: 1.3;

    @media (min-width: 640px) {
      font-size: 20px;
    }
  }

  .blurb {
    margin-top: 16px;
    max-width: 320px;
    color: var(--slate);
    line-height: 1.6;
  }

  nav {
    display: none;
    margin-top: 64px;

    @media (min-width: 1024px) {
      display: block;
    }
  }

  .nav-list {
    list-style: none;
    margin: 0;
    padding: 0;
    width: max-content;
  }
`;

const NavLink = styled.a`
  display: flex;
  align-items: center;
  padding: 12px 0;

  .indicator {
    margin-right: 16px;
    height: 1px;
    width: ${({ active }) => (active ? '64px' : '32px')};
    background: ${({ active }) => (active ? 'var(--lightest-slate)' : 'var(--lightest-navy)')};
    transition: all 0.15s ease;
  }

  .label {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${({ active }) => (active ? 'var(--lightest-slate)' : 'var(--dark-slate)')};
  }

  &:hover,
  &:focus-visible {
    color: inherit;

    .indicator {
      width: 64px;
      background: var(--lightest-slate);
    }

    .label {
      color: var(--lightest-slate);
    }
  }
`;

const SocialList = styled.ul`
  display: flex;
  align-items: center;
  gap: 20px;
  margin: 32px 0 0;
  padding: 0;
  list-style: none;

  a {
    color: var(--slate);

    svg {
      width: 24px;
      height: 24px;
    }

    &:hover,
    &:focus-visible {
      color: var(--lightest-slate);
    }
  }
`;

const Sidebar = () => {
  const [active, setActive] = useState('about');

  useEffect(() => {
    const ids = navLinks.map(link => link.url.replace('/#', ''));
    const sections = ids.map(id => document.getElementById(id)).filter(Boolean);

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActive(entry.target.id);
          }
        });
      },
      { rootMargin: '-40% 0px -50% 0px' },
    );

    sections.forEach(section => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  return (
    <StyledHeader>
      <div>
        <h1>
          <a href="/">Satender Rathore</a>
        </h1>
        <p className="title">Senior Software Engineer</p>
        <p className="blurb">
          I build products for the web and AI — frontend systems, LLMs, and agentic workflows.
        </p>

        <nav aria-label="In-page jump links">
          <ul className="nav-list">
            {navLinks.map(({ name, url }) => {
              const id = url.replace('/#', '');
              return (
                <li key={url}>
                  <NavLink href={url} active={active === id}>
                    <span className="indicator" />
                    <span className="label">{name}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>

      <SocialList>
        {socialMedia.map(({ name, url }) => (
          <li key={name}>
            <a href={url} aria-label={name} target="_blank" rel="noreferrer">
              <Icon name={name} />
            </a>
          </li>
        ))}
      </SocialList>
    </StyledHeader>
  );
};

export default Sidebar;
